import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import {
  useAdminUsers,
  useCreateAdminUser,
  useUpdateUserRole,
  useDeleteAdminUser,
} from "../../hooks/useAdminUsers";
import { AdminUser, AdminUserRole } from "../../types";

const AdminUserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { data, isLoading, error } = useAdminUsers();
  const createMutation = useCreateAdminUser();
  const updateRoleMutation = useUpdateUserRole();
  const deleteMutation = useDeleteAdminUser();

  // ダイアログ状態
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);

  // フォーム状態
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<AdminUserRole>("admin");

  // エラー/成功メッセージ
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setDisplayName("");
    setRole("admin");
    setFormError(null);
  };

  const handleOpenAddDialog = () => {
    resetForm();
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    resetForm();
  };

  const handleOpenEditDialog = (user: AdminUser) => {
    setSelectedUser(user);
    setRole(user.role);
    setFormError(null);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
    setFormError(null);
  };

  const handleOpenDeleteDialog = (user: AdminUser) => {
    setSelectedUser(user);
    setFormError(null);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
    setFormError(null);
  };

  const handleCreateUser = async () => {
    setFormError(null);

    if (!email || !password || !role) {
      setFormError("メールアドレス、パスワード、ロールは必須です");
      return;
    }

    if (password.length < 6) {
      setFormError("パスワードは6文字以上必要です");
      return;
    }

    try {
      await createMutation.mutateAsync({
        email,
        password,
        displayName: displayName || undefined,
        role,
      });
      setSuccessMessage("ユーザーを作成しました");
      handleCloseAddDialog();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (err instanceof Error) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } } }).response?.data?.message ||
          err.message;
        setFormError(errorMessage);
      } else {
        setFormError("ユーザーの作成に失敗しました");
      }
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedUser) return;
    setFormError(null);

    try {
      await updateRoleMutation.mutateAsync({
        uid: selectedUser.uid,
        role,
      });
      setSuccessMessage("ロールを更新しました");
      handleCloseEditDialog();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (err instanceof Error) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } } }).response?.data?.message ||
          err.message;
        setFormError(errorMessage);
      } else {
        setFormError("ロールの更新に失敗しました");
      }
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setFormError(null);

    try {
      await deleteMutation.mutateAsync(selectedUser.uid);
      setSuccessMessage("ユーザーを削除しました");
      handleCloseDeleteDialog();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (err instanceof Error) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } } }).response?.data?.message ||
          err.message;
        setFormError(errorMessage);
      } else {
        setFormError("ユーザーの削除に失敗しました");
      }
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("ja-JP");
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">ユーザー一覧の取得に失敗しました</Alert>;
  }

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}>
          管理者管理
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddDialog}>
          ユーザー追加
        </Button>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>メールアドレス</TableCell>
              <TableCell>表示名</TableCell>
              <TableCell>ロール</TableCell>
              <TableCell>最終ログイン</TableCell>
              <TableCell align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.users.map((user) => (
              <TableRow key={user.uid}>
                <TableCell>
                  {user.email}
                  {user.uid === currentUser?.uid && (
                    <Chip label="自分" size="small" color="default" sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell>{user.displayName || "-"}</TableCell>
                <TableCell>
                  <Chip label="管理者" color="primary" size="small" />
                </TableCell>
                <TableCell>{formatDate(user.lastSignInTime)}</TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenEditDialog(user)}
                    disabled={user.uid === currentUser?.uid}
                    title={
                      user.uid === currentUser?.uid
                        ? "自分自身のロールは変更できません"
                        : "ロールを変更"
                    }
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleOpenDeleteDialog(user)}
                    disabled={user.uid === currentUser?.uid}
                    title={user.uid === currentUser?.uid ? "自分自身は削除できません" : "削除"}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {data?.users.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  ユーザーがいません
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        合計: {data?.total || 0} ユーザー
      </Typography>

      {/* ユーザー追加ダイアログ */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>ユーザー追加</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="メールアドレス"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="パスワード"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required
              helperText="6文字以上"
            />
            <TextField
              label="表示名（任意）"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              fullWidth
            />
            <FormControl fullWidth required>
              <InputLabel>ロール</InputLabel>
              <Select
                value={role}
                label="ロール"
                onChange={(e) => setRole(e.target.value as AdminUserRole)}
              >
                <MenuItem value="admin">管理者</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>キャンセル</Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={createMutation.isLoading}
          >
            {createMutation.isLoading ? "作成中..." : "作成"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ロール編集ダイアログ */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>ロール変更</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <DialogContentText sx={{ mb: 2 }}>
            {selectedUser?.email} のロールを変更します
          </DialogContentText>
          <FormControl fullWidth required>
            <InputLabel>ロール</InputLabel>
            <Select
              value={role}
              label="ロール"
              onChange={(e) => setRole(e.target.value as AdminUserRole)}
            >
              <MenuItem value="admin">管理者</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>キャンセル</Button>
          <Button
            onClick={handleUpdateRole}
            variant="contained"
            disabled={updateRoleMutation.isLoading}
          >
            {updateRoleMutation.isLoading ? "更新中..." : "更新"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>ユーザー削除の確認</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <DialogContentText>
            {selectedUser?.email} の管理者権限を削除しますか？
            <br />
            <Typography variant="caption" color="text.secondary">
              ※ユーザーアカウント自体は削除されません
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>キャンセル</Button>
          <Button
            onClick={handleDeleteUser}
            color="error"
            variant="contained"
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? "削除中..." : "削除"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUserManagement;
